import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { camerasAPI, authAPI } from '../../utils/api';
import TelegramLinkModal from '../../components/TelegramLinkModal/TelegramLinkModal';
import Loader from '../../components/Loader/Loader';
import './Profile.css';

interface Camera {
  id: number;
  name: string;
  rtsp_uri: string;
  camera_id: string | null;
  area_id: number;
}

// Маска для телефона формата +7 (999) 999-99-99
const formatPhone = (value: string): string => {
  // Оставляем только цифры
  let digits = value.replace(/\D/g, '');

  // Убираем ведущую 8 или 7 — всегда приводим к формату +7
  if (digits.startsWith('8')) {
    digits = '7' + digits.slice(1);
  }
  if (!digits.startsWith('7')) {
    digits = '7' + digits;
  }

  // Ограничиваем максимум 11 цифрами (7 + 10 цифр номера)
  digits = digits.slice(0, 11);

  const parts = [];
  parts.push('+7');

  const code = digits.slice(1, 4); // 3 цифры кода
  if (code) {
    parts.push(` (${code}`);
    if (code.length === 3) parts[parts.length - 1] += ')';
  }

  const first = digits.slice(4, 7); // первые 3 цифры
  if (first) {
    parts.push(` ${first}`);
  }

  const second = digits.slice(7, 9); // следующие 2
  if (second) {
    parts.push(`-${second}`);
  }

  const third = digits.slice(9, 11); // последние 2
  if (third) {
    parts.push(`-${third}`);
  }

  return parts.join('');
};

const Profile: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { requireSubscription, openSubscriptionModal, hasSubscription } = useSubscription();
  const navigate = useNavigate();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(true);
  
  // Состояния для редактирования полей
  const [editingField, setEditingField] = useState<'email' | 'name' | 'phone' | null>(null);
  const [editValues, setEditValues] = useState({
    email: '',
    name: '',
    phone: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string>('');

  // Состояния для модалки смены пароля
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    password: '',
    password_confirmation: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string>('');

  // Telegram integration modal
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  useEffect(() => {
    const loadCameras = async () => {
      if (authLoading || !hasSubscription) {
        setCamerasLoading(false);
        return;
      }

      try {
        setCamerasLoading(true);
        const response = await camerasAPI.getAll();
        if (response.success) {
          setCameras(response.cameras || []);
        }
      } catch (error) {
        console.error('Ошибка загрузки камер:', error);
      } finally {
        setCamerasLoading(false);
      }
    };

    loadCameras();
  }, [authLoading, hasSubscription]);

  // Инициализация значений для редактирования
  useEffect(() => {
    if (user) {
      setEditValues({
        email: user.email || '',
        name: user.name || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const startEdit = (field: 'email' | 'name' | 'phone') => {
    setEditingField(field);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditError('');
    if (user) {
      setEditValues({
        email: user.email || '',
        name: user.name || '',
        phone: user.phone || '',
      });
    }
  };

  const saveField = async (field: 'email' | 'name' | 'phone') => {
    if (!user) return;

    setEditLoading(true);
    setEditError('');

    try {
      const updateData: { [key: string]: string } = {};
      updateData[field] = editValues[field].trim();

      // Валидация
      if (field === 'email' && !updateData.email) {
        setEditError('Email не может быть пустым');
        setEditLoading(false);
        return;
      }
      if (field === 'name' && !updateData.name) {
        setEditError('Имя не может быть пустым');
        setEditLoading(false);
        return;
      }

      const response = await authAPI.updateProfile(updateData);
      
      if (response.success && response.user) {
        // Обновляем пользователя через повторный запрос
        const userResponse = await authAPI.getUser();
        if (userResponse.success && userResponse.user) {
          // Обновляем через перезагрузку страницы для синхронизации с AuthContext
          window.location.reload();
        }
        setEditingField(null);
      } else {
        setEditError(response.message || 'Ошибка обновления');
      }
    } catch (error: any) {
      console.error('Ошибка обновления профиля:', error);
      setEditError(error.message || 'Ошибка обновления профиля');
    } finally {
      setEditLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    
    if (passwordData.password !== passwordData.password_confirmation) {
      setPasswordError('Пароли не совпадают');
      return;
    }

    if (passwordData.password.length < 8) {
      setPasswordError('Пароль должен содержать минимум 8 символов');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await authAPI.changePassword({
        old_password: passwordData.old_password,
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation,
      });

      if (response.success) {
        setIsPasswordModalOpen(false);
        setPasswordData({
          old_password: '',
          password: '',
          password_confirmation: '',
        });
        alert('Пароль успешно изменен');
      } else {
        setPasswordError(response.message || 'Ошибка смены пароля');
      }
    } catch (error: any) {
      console.error('Ошибка смены пароля:', error);
      setPasswordError(error.message || 'Ошибка смены пароля');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (authLoading) {
    return <Loader />;
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  // Показываем только первые 3 камеры
  const displayedCameras = cameras.slice(0, 3);

  return (
    <div className="profile-page">
      <main className="profile-main container">
        <header className="profile-header">
          <h1 className="profile-header__title">Профиль</h1>
          <div className="profile-header__user">
            <div className="profile-header__name">{user.name}</div>
            <div className="profile-header__meta">
              <div className="profile-header__balance">
                Баланс: {(user.balance ?? 0).toLocaleString('ru-RU')} ₽
              </div>
              <button
                type="button"
                className="profile-header__tariff profile-header__tariff--btn"
                onClick={() => navigate('/balance')}
              >
                {/* Оплата теперь за каждую камеру — показываем, сколько камер оплачено */}
                {user.paid_cameras_count
                  ? `Оплачено камер: ${user.paid_cameras_count} · ${(user.camera_monthly_price ?? 1000).toLocaleString('ru-RU')} ₽ / мес за камеру`
                  : `Камера — ${(user.camera_monthly_price ?? 1000).toLocaleString('ru-RU')} ₽ / мес`}
              </button>
            </div>
          </div>
        </header>

        <section className="profile-content">
          <div className="profile-column">
            <article className="profile-card profile-card--left">
              <h2 className="profile-card__title">Личные данные</h2>

              {/* Email */}
              <div className="profile-field">
                {editingField === 'email' ? (
                  <div className="profile-field__editor">
                    <input
                      type="email"
                      value={editValues.email}
                      onChange={(e) => setEditValues({ ...editValues, email: e.target.value })}
                      className="profile-field__input"
                      autoFocus
                    />
                    {editError && <div className="profile-field__error">{editError}</div>}
                    <div className="profile-field__editor-actions">
                      <button
                        type="button"
                        className="profile-field__save-btn"
                        onClick={() => saveField('email')}
                        disabled={editLoading}
                      >
                        {editLoading ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="profile-field__cancel-btn"
                        onClick={cancelEdit}
                        disabled={editLoading}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="profile-field__value">{user.email}</div>
                    <button
                      type="button"
                      className="profile-field__edit"
                      aria-label="Редактировать email"
                      onClick={() => startEdit('email')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/edit.svg`}
                        alt=""
                        className="profile-field__edit-icon"
                        aria-hidden="true"
                      />
                    </button>
                  </>
                )}
              </div>

              {/* Name */}
              <div className="profile-field">
                {editingField === 'name' ? (
                  <div className="profile-field__editor">
                    <input
                      type="text"
                      value={editValues.name}
                      onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                      className="profile-field__input"
                      autoFocus
                    />
                    {editError && <div className="profile-field__error">{editError}</div>}
                    <div className="profile-field__editor-actions">
                      <button
                        type="button"
                        className="profile-field__save-btn"
                        onClick={() => saveField('name')}
                        disabled={editLoading}
                      >
                        {editLoading ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="profile-field__cancel-btn"
                        onClick={cancelEdit}
                        disabled={editLoading}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="profile-field__value">{user.name}</div>
                    <button
                      type="button"
                      className="profile-field__edit"
                      aria-label="Редактировать имя"
                      onClick={() => startEdit('name')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/edit.svg`}
                        alt=""
                        className="profile-field__edit-icon"
                        aria-hidden="true"
                      />
                    </button>
                  </>
                )}
              </div>

              {/* Phone */}
              <div className="profile-field">
                {editingField === 'phone' ? (
                  <div className="profile-field__editor">
                    <input
                      type="tel"
                      value={editValues.phone}
                      onChange={(e) =>
                        setEditValues({
                          ...editValues,
                          phone: formatPhone(e.target.value),
                        })
                      }
                      className="profile-field__input"
                      placeholder="+7 (999) 999-99-99"
                      autoFocus
                    />
                    {editError && <div className="profile-field__error">{editError}</div>}
                    <div className="profile-field__editor-actions">
                      <button
                        type="button"
                        className="profile-field__save-btn"
                        onClick={() => saveField('phone')}
                        disabled={editLoading}
                      >
                        {editLoading ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="profile-field__cancel-btn"
                        onClick={cancelEdit}
                        disabled={editLoading}
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="profile-field__value">{user.phone || 'Телефон не указан'}</div>
                    <button
                      type="button"
                      className="profile-field__edit"
                      aria-label="Редактировать телефон"
                      onClick={() => startEdit('phone')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/edit.svg`}
                        alt=""
                        className="profile-field__edit-icon"
                        aria-hidden="true"
                      />
                    </button>
                  </>
                )}
              </div>
            </article>

            <div className="profile-actions-row">
              <button
                type="button"
                className="profile-button profile-button--secondary"
                onClick={() => setIsPasswordModalOpen(true)}
              >
                Поменять пароль
              </button>
              <button
                type="button"
                className="profile-button profile-button--secondary"
                onClick={() => {
                  setIsTelegramModalOpen(true);
                }}
              >
                Уведомления в Telegram
              </button>
              <button
                type="button"
                className="profile-button profile-button--secondary"
                onClick={() => navigate('/balance')}
              >
                Оплата камер
              </button>
            </div>
          </div>

          <article className="profile-card profile-card--right">
            <h2 className="profile-card__title">Мои камеры</h2>

            {camerasLoading ? (
              <div className="ui-loading">Загрузка камер...</div>
            ) : !hasSubscription ? (
              <div className="ui-empty">
                <span>Камеры доступны после подключения подписки.</span>
                <button type="button" className="profile-link-btn" onClick={() => openSubscriptionModal()}>
                  Подключить
                </button>
              </div>
            ) : displayedCameras.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">У вас пока нет камер</span>
                <span>Добавьте камеру в любом участке на дашборде</span>
              </div>
            ) : (
              displayedCameras.map((camera, index) => (
                <div key={camera.id} className="profile-field">
                  <div className="profile-field__value">{camera.name}</div>
                  <button
                    type="button"
                    className="profile-field__edit"
                    aria-label={`Редактировать камеру ${index + 1}`}
                    onClick={() => requireSubscription()}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/icons/edit.svg`}
                      alt=""
                      className="profile-field__edit-icon"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              ))
            )}

            {cameras.length > 3 && (
              <div className="profile-card__actions profile-card__actions--full">
                <button 
                  type="button" 
                  className="profile-button profile-button--primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Посмотреть все камеры ({cameras.length})
                </button>
              </div>
            )}
          </article>
        </section>
      </main>

      {/* Модалка смены пароля */}
      {isPasswordModalOpen && (
        <div className="profile-modal" role="dialog" aria-modal="true">
          <div
            className="profile-modal__backdrop"
            onClick={() => !passwordLoading && setIsPasswordModalOpen(false)}
          />
          <div className="profile-modal__dialog">
            <header className="profile-modal__header">
              <h2 className="profile-modal__title">Изменить пароль</h2>
              <button
                type="button"
                className="profile-modal__close"
                aria-label="Закрыть"
                onClick={() => !passwordLoading && setIsPasswordModalOpen(false)}
                disabled={passwordLoading}
              >
                ×
              </button>
            </header>

            <form className="profile-modal__body" onSubmit={handleChangePassword}>
              <div className="profile-modal__field">
                <label className="profile-modal__label">Текущий пароль</label>
                <input
                  type="password"
                  className="profile-modal__input"
                  value={passwordData.old_password}
                  onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                  required
                  disabled={passwordLoading}
                />
              </div>

              <div className="profile-modal__field">
                <label className="profile-modal__label">Новый пароль</label>
                <input
                  type="password"
                  className="profile-modal__input"
                  value={passwordData.password}
                  onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                  required
                  minLength={8}
                  disabled={passwordLoading}
                />
              </div>

              <div className="profile-modal__field">
                <label className="profile-modal__label">Подтвердите новый пароль</label>
                <input
                  type="password"
                  className="profile-modal__input"
                  value={passwordData.password_confirmation}
                  onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                  required
                  minLength={8}
                  disabled={passwordLoading}
                />
              </div>

              {passwordError && (
                <div className="profile-modal__error">{passwordError}</div>
              )}

              <footer className="profile-modal__footer">
                <button
                  type="button"
                  className="profile-modal__button profile-modal__button--secondary"
                  onClick={() => setIsPasswordModalOpen(false)}
                  disabled={passwordLoading}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="profile-modal__button profile-modal__button--primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Сохранение...' : 'Изменить пароль'}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {isTelegramModalOpen && <TelegramLinkModal onClose={() => setIsTelegramModalOpen(false)} />}
    </div>
  );
};

export default Profile;
